import { useState, useEffect, ChangeEvent, Fragment } from 'react';
import * as yup from 'yup';
import moment from 'moment';
import timezone from 'moment-timezone';
import i18n from 'helpers/i18n';
import messages from './messages';
import { Input } from 'components/Measure/Input';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { Assessment } from 'types/api/envScales/Assessment';
import TimeRangeInput from 'components/Measure/TimeRangeInput';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { updateObservationCycle } from 'actions/envScales/observationCycles';
import { FormRow, FormColumn } from './Styled';
import {
  useValidations,
  useFieldError,
  useServerFieldError,
  getSubsectionStatus
} from 'components/Measure/View/ValidationsContext';
import {
  getAdultWarnings,
  getChildrenWarnings,
  getCycleTimeWarnings,
  valueForField,
  numericFieldsWarnings,
  getInitialState,
  NumericFieldStateType,
  EIGHT_MINUTES_IN_MILLISECONDS,
  THIRTY_MINUTES_IN_MILLISECONDS
} from './utils';
import Warning from 'components/Measure/Warning';
import * as R from 'ramda';

import MetaDataFields from '../MetaDataFields';
import { SectionCollapse } from 'components/SectionCollapse';
import MetaDataCLASSCycles from './MetaDataCLASSCycles';

interface CycleDetailsFormProps {
  /** Observation Cycle. */
  cycle: ObservationCycle;

  /** CLASS Assessment. */
  assessment: Assessment;

  /** Environmental Scales Age Level. */
  ageLevel: AgeLevel;

  /** Custom className for wrapper component. */
  className?: null | string;
}

function CycleDetailsForm({
  assessment,
  cycle,
  ageLevel,
  className
}: CycleDetailsFormProps) {
  const [touchedTime, setTouchedTime] = useState(false);
  const [touchedAdults, setTouchedAdults] = useState(false);
  const [touchedChildren, setTouchedChildren] = useState(false);
  const [state, setState] = useState(
    getInitialState(cycle, assessment.local_time_zone)
  );
  const [errors, setErrors] = useState<{ cycleTime?: string }>({});
  const [cycleTimeWarnings, setCycleTimeWarnings] = useState<Array<string>>([]);
  const [adultsWarnings, setAdultsWarnings] = useState<Array<string>>([]);
  const [childrenWarnings, setChildrenWarnings] = useState<Array<string>>([]);
  const {
    sections,
    errors: validationErrors,
    resolvedPaths,
    showErrorStatus,
    reportFieldResolution
  } = useValidations();

  const adultsFieldError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.adults`,
    state.adults,
    yup.number().moreThan(0).required()
  );
  const childrenFieldError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.children`,
    state.children,
    yup.number().moreThan(0).required()
  );

  // A server-defaulted time can already look well-formed, so this only
  // resolves once the user has actually edited it.
  const cycleTimePath = `cycles.${cycle.cycle_number}.details.cycle_time`;
  const cycleTimeServerError = useFieldError(cycleTimePath);
  const cycleTimeSchema = yup.array().of(yup.string().required());
  const cycleTimeIsValidNow = cycleTimeSchema.isValidSync([
    state.start_time,
    state.end_time
  ]);
  // The touched guard only applies when there's a real server error to
  // distrust a stale-looking value against; otherwise always report live.
  const cycleTimeIsResolved =
    cycleTimeIsValidNow && (!cycleTimeServerError || touchedTime);

  useEffect(() => {
    reportFieldResolution(cycleTimePath, cycleTimeIsResolved);
  }, [cycleTimePath, cycleTimeIsResolved, reportFieldResolution]);

  const cycleTimeFieldError =
    !cycleTimeServerError || cycleTimeIsResolved ? null : cycleTimeServerError;

  const numericFields = ageLevel.meta_data_categories.filter(
    category => category.data_type === 'numeric'
  );
  const hasNumericFields = numericFields.length > 0;
  const numericFieldMapping = numericFields.map(field => {
    return [field.t_name, valueForField(field.id, cycle)];
  });
  const [numericFieldsState, setNumericFieldsState] =
    useState<NumericFieldStateType>(Object.fromEntries(numericFieldMapping));
  const numberOfNumericFieldsInARow = 2;
  const groupedNumericFields = R.splitEvery(
    numberOfNumericFieldsInARow,
    numericFields
  );

  useDebouncedEffect(
    () => {
      if (touchedTime || touchedAdults || touchedChildren) {
        const observationId = assessment.observation!.id;

        const payload = {
          ...state,
          start_time: timezone.tz(
            `${assessment.taken_at} ${state.start_time}`,
            assessment.local_time_zone
          ),
          end_time: timezone.tz(
            `${assessment.taken_at} ${state.end_time}`,
            assessment.local_time_zone
          )
        };

        updateObservationCycle(observationId, cycle.id, payload);
      }
    },
    300,
    [touchedTime, touchedAdults, touchedChildren, state]
  );

  useDebouncedEffect(
    () => {
      if (touchedTime) {
        setCycleTimeWarnings(
          getCycleTimeWarnings(
            assessment.taken_at,
            state.start_time,
            state.end_time
          )
        );
      }
    },
    800,
    [touchedTime, state.start_time, state.end_time]
  );

  useDebouncedEffect(
    () => {
      if (touchedAdults) {
        setAdultsWarnings(getAdultWarnings(state.adults as number));
      }
    },
    800,
    [touchedAdults, state.adults]
  );

  useDebouncedEffect(
    () => {
      if (touchedChildren) {
        setChildrenWarnings(getChildrenWarnings(state.children as number));
      }
    },
    800,
    [touchedChildren, state.children]
  );

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    event.target.focus();
    const stateUpdated = { ...state, [event.target.name]: event.target.value };
    setState(stateUpdated);

    if (event.target.name === 'adults') {
      setTouchedAdults(true);
    } else if (event.target.name === 'children') {
      setTouchedChildren(true);
    }
  }

  function validateTime(times: Array<String>) {
    const [start_time, end_time] = times;

    if (start_time && end_time) {
      const diff = moment
        .utc(`${assessment.taken_at} ${end_time}`)
        .diff(moment.utc(`${assessment.taken_at} ${start_time}`));

      if (diff < EIGHT_MINUTES_IN_MILLISECONDS) {
        return setErrors({
          ...errors,
          cycleTime: i18n.ft(messages.shortCycleTime)
        });
      } else if (diff > THIRTY_MINUTES_IN_MILLISECONDS) {
        return setErrors({
          ...errors,
          cycleTime: i18n.ft(messages.longCycleTime)
        });
      }

      setCycleTimeWarnings(
        getCycleTimeWarnings(
          assessment.taken_at,
          state.start_time,
          state.end_time
        )
      );
    } else if (R.isEmpty(start_time)) {
      return setErrors({
        ...errors,
        cycleTime: i18n.ft(messages.errors.startCycleTime.required)
      });
    } else if (R.isEmpty(end_time)) {
      return setErrors({
        ...errors,
        cycleTime: i18n.ft(messages.errors.endCycleTime.required)
      });
    }

    setErrors({ ...errors, cycleTime: undefined });
  }

  function handleTimeChange(value: string[]) {
    const [start_time, end_time] = value;
    setState({ ...state, start_time, end_time });
    setTouchedTime(true);
    validateTime([start_time, end_time]);
  }

  function cycleTimeErrorMessage() {
    // Local checks take priority once touched; the server error still
    // shows once those clear, rather than being hidden for the session.
    if (touchedTime && errors.cycleTime) {
      return errors.cycleTime;
    }

    return cycleTimeFieldError?.message ?? null;
  }

  const commonFields = () => (
    <>
      <Warning
        warningTexts={adultsWarnings}
        maxWidth={290}
        dismissible
        onDismiss={() => setAdultsWarnings([])}
      >
        <Input
          min="0"
          fluid
          required
          type="number"
          name="adults"
          label={i18n.ft(messages.adults)}
          onChange={handleChange}
          onBlur={() =>
            setAdultsWarnings(getAdultWarnings(state.adults as number))
          }
          value={state.adults}
          errorMessage={adultsFieldError?.message ?? null}
        />
      </Warning>

      <Warning
        warningTexts={childrenWarnings}
        maxWidth={250}
        dismissible
        onDismiss={() => setChildrenWarnings([])}
      >
        <Input
          min="0"
          fluid
          required
          type="number"
          name="children"
          label={i18n.ft(messages.children)}
          onChange={handleChange}
          onBlur={() =>
            setChildrenWarnings(getChildrenWarnings(state.children as number))
          }
          value={state.children}
          errorMessage={childrenFieldError?.message ?? null}
        />
      </Warning>
    </>
  );

  return (
    <SectionCollapse
      attached
      className={className}
      title={i18n.ft(messages.details)}
      subtitle={i18n.ft(messages.sectionDescription)}
      status={
        !showErrorStatus || !sections
          ? null
          : getSubsectionStatus(
              sections.cycles[cycle.cycle_number],
              'details',
              showErrorStatus,
              {
                errors: validationErrors,
                resolvedPaths,
                prefix: `cycles.${cycle.cycle_number}.details`
              }
            )
      }
      closeOthersOnOpen
    >
      <FormRow>
        <Warning
          warningTexts={cycleTimeWarnings}
          maxWidth={290}
          dismissible
          onDismiss={() => setCycleTimeWarnings([])}
        >
          <TimeRangeInput
            required
            label={i18n.ft(messages.cycleTime)}
            value={[state.start_time, state.end_time]}
            onChange={handleTimeChange}
            errorMessage={cycleTimeErrorMessage()}
            onBlur={validateTime}
          />
        </Warning>

        {!hasNumericFields && <>{commonFields()}</>}
      </FormRow>

      {hasNumericFields && (
        <>
          <FormColumn>{commonFields()}</FormColumn>

          {groupedNumericFields.map((fieldsRow, index) => {
            const [firstField] = fieldsRow;
            return (
              <Fragment key={`numeric_fields_row_${index}`}>
                <FormColumn>
                  {fieldsRow.map((field, fieldIndex) => (
                    <MetaDataFields
                      key={`numeric_field_${fieldIndex}`}
                      fields={field}
                      defaultValue={valueForField(field.id, cycle)}
                      assessment={assessment}
                      cycle={cycle}
                      onChange={value =>
                        setNumericFieldsState({
                          ...numericFieldsState,
                          [field.t_name]: value
                        })
                      }
                    />
                  ))}
                </FormColumn>
                <Warning
                  warningTexts={numericFieldsWarnings(
                    numericFieldsState,
                    state,
                    firstField
                  )}
                  floating={false}
                />
              </Fragment>
            );
          })}
        </>
      )}

      <MetaDataCLASSCycles
        cycle={cycle}
        assessment={assessment}
        ageLevel={ageLevel}
        observationId={assessment.observation!.id}
        values={cycle.cycle_meta_data_values}
      />
    </SectionCollapse>
  );
}

export default CycleDetailsForm;
